/**
 * A slideshow playing inside a cell.
 *
 * The same shape of job as the video player, without hls.js: fetch an
 * encrypted blob, decrypt it in memory, show it, move on. Nothing decrypted is
 * ever written anywhere — each slide becomes an object URL that lives in
 * memory and is revoked the moment it is no longer on screen.
 *
 * Which size to fetch is decided by measuring the cell, which a video cannot
 * do (it has already committed to a bitrate ladder before the first frame).
 * A quarter-screen pane genuinely wants the small copy.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  advanceSlide,
  nextSlideSpeed,
  pickForWidth,
  slideSeconds,
  type Item,
  type VideoPayload,
} from '../types';
import { useVault } from '../vault';

/** Slides held decrypted ahead of the current one. */
const LOOKAHEAD = 2;

/**
 * Ignore resizes smaller than this fraction before re-choosing a rung.
 *
 * Dragging the splitter fires continuously, and every intermediate width would
 * otherwise be a candidate for a different rung.
 */
const RESIZE_TOLERANCE = 1.25;

interface Props {
  video: Item<VideoPayload>;
  /** Called when the last slide has had its turn, so the queue can advance. */
  onEnded(): void;
  /** Fades with the cell, matching how the video player hands over. */
  visible: boolean;
  /** The cell the library and the keyboard are aimed at. */
  active?: boolean;
  /** Seconds per slide, overriding what the CLI stored. */
  speed?: number;
  onSpeed?(seconds: number): void;
}

export function SlideshowCell({ video, onEnded, visible, active, speed, onSpeed }: Props) {
  const { slideUrl, cached } = useVault();
  /**
   * Held in a ref, never in the effect's dependencies.
   *
   * It changes once per blob during a preload; as a dependency it would
   * restart the fetch loop — and the slide timer with it — on every one.
   */
  const cachedRef = useRef(cached);
  cachedRef.current = cached;
  const slides = useMemo(() => video.payload.slides ?? [], [video.payload.slides]);
  const seconds = slideSeconds(video.payload, speed);

  const boxRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Object URL per slide index, for the small window we keep decrypted. */
  const [urls, setUrls] = useState<Record<number, string>>({});
  /**
   * Width to fetch for, in device pixels.
   *
   * Starts at zero so nothing is fetched until the cell has been measured —
   * guessing and then correcting would mean fetching two copies of slide one.
   */
  const [wantWidth, setWantWidth] = useState(0);

  // Everything decrypted is released together, so a cell that goes away or a
  // vault that locks leaves no plaintext behind.
  const urlsRef = useRef(urls);
  urlsRef.current = urls;
  useEffect(
    () => () => {
      for (const url of Object.values(urlsRef.current)) URL.revokeObjectURL(url);
    },
    [],
  );

  // A new slideshow in the same cell starts from the beginning, and the
  // previous one's decrypted slides go immediately.
  useEffect(() => {
    setIndex(0);
    setError(null);
    setUrls((previous) => {
      for (const url of Object.values(previous)) URL.revokeObjectURL(url);
      return {};
    });
  }, [video.uuid]);

  /** Measure the cell, in device pixels, with a tolerance band. */
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') {
      setWantWidth(960);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width <= 0) return;
      // Capped at 2: beyond that the extra pixels are past what anyone can
      // see, and on a phone they triple the bytes for nothing.
      const wanted = width * Math.min(window.devicePixelRatio || 1, 2);
      setWantWidth((current) => {
        if (current === 0) return wanted;
        const ratio = wanted / current;
        // Only ever upward. Shrinking a cell must not swap a sharp image for a
        // soft one: the bytes are already spent and the result looks broken.
        return ratio > RESIZE_TOLERANCE ? wanted : current;
      });
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  /** Decrypt the current slide and the next few, and drop the rest. */
  useEffect(() => {
    if (!slides.length || wantWidth === 0) return;
    let live = true;

    const wanted: number[] = [];
    for (let step = 0; step <= LOOKAHEAD; step += 1) {
      const at = index + step;
      if (at < slides.length) wanted.push(at);
    }

    void (async () => {
      for (const at of wanted) {
        if (!live) return;
        if (urlsRef.current[at]) continue;
        // Prefer a copy already on the device, even a smaller one. Cache
        // reconciliation keeps a single rung per slide, so fetching a
        // different size here would have it delete the one we just used and
        // download the same picture again on the next pass.
        const all = slides[at]?.variants ?? [];
        const onDisk = all.filter((v) => cachedRef.current.has(v.blob));
        const variant = pickForWidth(onDisk.length ? onDisk : all, wantWidth);
        if (!variant) continue;
        try {
          const url = await slideUrl(variant.blob);
          if (!live || !url) {
            if (url) URL.revokeObjectURL(url);
            return;
          }
          setUrls((previous) => {
            // Lost a race, or already dropped: do not leak the loser.
            if (previous[at]) {
              URL.revokeObjectURL(url);
              return previous;
            }
            return { ...previous, [at]: url };
          });
        } catch {
          if (live && at === index) setError('could not decrypt this slide');
        }
      }
    })();

    // Anything behind the current slide is done with. Kept as one pass rather
    // than per-slide cleanup so a jump backwards does not free what it needs.
    setUrls((previous) => {
      const keep: Record<number, string> = {};
      let changed = false;
      for (const [key, url] of Object.entries(previous)) {
        const at = Number(key);
        if (at >= index && at <= index + LOOKAHEAD) keep[at] = url;
        else {
          URL.revokeObjectURL(url);
          changed = true;
        }
      }
      return changed ? keep : previous;
    });

    return () => {
      live = false;
    };
  }, [index, slides, slideUrl, wantWidth]);

  const current = urls[index] ?? null;

  /** Advance on a timer, but only once the slide is actually on screen. */
  useEffect(() => {
    if (paused || !current || !slides.length) return;
    // Keyed on `index` as well, so stepping by hand restarts the wait rather
    // than cutting the new slide short with the old one's remaining time.
    const timer = window.setTimeout(() => {
      const next = advanceSlide(index, slides.length);
      setIndex(next.index);
      // Told either way. A queue moves on; a cell holding only this starts it
      // again, which is what the wrap is for.
      if (next.ended) onEnded();
    }, seconds * 1000);
    return () => window.clearTimeout(timer);
  }, [current, index, onEnded, paused, seconds, slides.length]);

  const step = useCallback(
    (delta: number) => {
      setIndex((at) => Math.min(slides.length - 1, Math.max(0, at + delta)));
    },
    [slides.length],
  );

  /**
   * Arrow keys drive the slideshow in the selected cell.
   *
   * Only the selected one: with four on the wall, keys that moved all of them
   * would be useless, and keys that moved an arbitrary one worse. Ignored
   * while typing, so a rename field keeps its own arrows.
   */
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (event.key === 'ArrowRight') step(1);
      else if (event.key === 'ArrowLeft') step(-1);
      else if (event.key === ' ') setPaused((p) => !p);
      else return;
      // Only once the key is one of ours: the page still scrolls otherwise.
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, step]);

  if (!slides.length) {
    return <div className="cell__status cell__status--error">this slideshow has no slides</div>;
  }

  return (
    <div className="slides" ref={boxRef} style={{ opacity: visible ? 1 : 0 }}>
      {current ? (
        // Keyed by index so each slide mounts fresh and plays the fade-in,
        // rather than swapping the source under a static element.
        <img key={index} className="slides__image" src={current} alt="" draggable={false} />
      ) : (
        <div className="cell__status">{error ?? 'decrypting…'}</div>
      )}

      <div className="slides__controls">
        <button
          type="button"
          className="chip"
          onClick={() => step(-1)}
          disabled={index === 0}
          title="Previous slide"
          aria-label="Previous slide"
        >
          ‹
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => setPaused((p) => !p)}
          title={paused ? 'Resume' : 'Pause'}
          aria-label={paused ? 'Resume' : 'Pause'}
        >
          {paused ? '▶' : '❚❚'}
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => step(1)}
          disabled={index + 1 >= slides.length}
          title="Next slide"
          aria-label="Next slide"
        >
          ›
        </button>
        <span className="slides__count">
          {index + 1}/{slides.length}
        </span>
        {onSpeed && (
          <button
            type="button"
            className="chip"
            onClick={() => onSpeed(nextSlideSpeed(seconds))}
            title="Seconds per slide. Arrow keys step by hand; space pauses."
          >
            {seconds}s
          </button>
        )}
      </div>
    </div>
  );
}
